// Portal subdomain router (portal.firstcitypoly.edu.ng)
import { createRouter, createWebHistory } from "vue-router";
import { createLogger } from "@/utils/logger";
import { canCompleteOutstandingOLevel } from "@/utils/applicationOLevelUtils";
import store from "../store";

// Student App Views
import AppFinance from "@/views/student/AppFinance.vue";
import AppDashboard from "@/views/student/AppDashboard.vue";
import ApplicationFormView from "@/views/student/ApplicationForm.vue";
import AppLoginView from "@/views/student/AppLoginView.vue";
import ForgotPasswordView from "@/views/student/ForgotPasswordView.vue";
import ResetPasswordView from "@/views/student/ResetPasswordView.vue";
import AppResources from "@/views/student/AppResources.vue";
import AppAcademics from "@/views/student/AppAcademics.vue";
import AppSettings from "@/views/student/AppSettings.vue";

// Layouts
import AppLayout from "../layouts/AppLayout.vue";

const logger = createLogger("PortalRouter");

const routes = [
  {
    path: "/",
    component: AppLayout,
    meta: { hideHeader: true, hideFooter: true, requiresAuth: true },
    children: [
      {
        path: "",
        name: "login",
        component: AppLoginView,
        meta: {
          title: "Student Login",
          hideAppHeader: true,
          hideAppSidebar: true,
          guest: true,
        },
      },
      {
        path: "forgot-password",
        name: "forgotPassword",
        component: ForgotPasswordView,
        meta: {
          title: "Forgot Password",
          hideAppHeader: true,
          hideAppSidebar: true,
          guest: true,
        },
      },
      {
        path: "reset-password",
        name: "resetPassword",
        component: ResetPasswordView,
        meta: {
          title: "Reset Password",
          hideAppHeader: true,
          hideAppSidebar: true,
          guest: true,
          allowAuthenticated: true,
        },
      },
      {
        path: "dashboard",
        name: "app",
        component: AppDashboard,
        meta: { title: "Dashboard", requiresAuth: true },
      },
      {
        path: "application",
        name: "application",
        component: ApplicationFormView,
        meta: { title: "Application", requiresAuth: true },
        beforeEnter: (to, from, next) => {
          const application = store.state.session?.application;
          if (application && application.status !== "draft") {
            next({ name: "app" });
          } else {
            next();
          }
        },
      },
      {
        path: "appForm/complete-olevel",
        name: "applicationOLevelCompletion",
        component: ApplicationFormView,
        meta: { title: "Complete Application Form", requiresAuth: true },
        beforeEnter: (to, from, next) => {
          const biodata = store.state.session?.application;
          if (!canCompleteOutstandingOLevel(biodata)) {
            next({ name: "resources" });
          } else {
            next();
          }
        },
      },
      {
        path: "finance",
        name: "finance",
        component: AppFinance,
        meta: { title: "Finance", requiresAuth: true },
      },
      {
        path: "academics",
        name: "academics",
        component: AppAcademics,
        meta: { title: "Academics", requiresAuth: true },
      },
      {
        path: "resources",
        name: "resources",
        component: AppResources,
        meta: { title: "Resources", requiresAuth: true },
      },
      {
        path: "settings",
        name: "settings",
        component: AppSettings,
        meta: { title: "Settings", requiresAuth: true },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory("/"),
  routes,
  scrollBehavior(to) {
    if (to.hash && !to.hash.startsWith("#token=")) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    } else {
      return { top: 0 };
    }
  },
});

// Navigation Guard for Authentication Handling
router.beforeEach(async (to, from, next) => {
  store.commit("setLoading", true);

  // Use the route's own meta, not parent's, to check guest status
  const isGuestRoute = to.meta.guest === true;

  // Check auth status once per page load (always check to know if user is logged in)
  // This is needed even for guest routes to redirect logged-in users away from login
  if (!store.state.authChecked) {
    try {
      await store.dispatch("checkAuthStatus");
    } catch (error) {
      // 401 errors are expected when not logged in - already handled silently by interceptors
      logger.debug("Auth check failed (expected if not logged in):", error);
      store.commit("setAuthChecked", true);
    }
  }

  const isStudentLoggedIn = store.getters.isAuthenticated;

  // Guest routes (login): allow if not logged in, redirect to dashboard if logged in
  if (isGuestRoute) {
    if (isStudentLoggedIn && to.meta.allowAuthenticated !== true) {
      store.commit("setLoading", false);
      return next({ name: "app" });
    }
    store.commit("setLoading", false);
    return next();
  }

  // Protected routes: require student auth
  if (!isStudentLoggedIn) {
    store.commit("setLoading", false);
    return next({ name: "login" });
  }

  store.commit("setLoading", false);
  next();
});
router.afterEach(() => {
  store.commit("setLoading", false);
});

export default router;
